import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    CheckboxVisibility,
    DetailsListLayoutMode,
    IColumn,
    Selection,
    SelectionMode,
} from "office-ui-fabric-react/lib/DetailsList";
import { MarqueeSelection } from "office-ui-fabric-react/lib/MarqueeSelection";
import { ShimmeredDetailsList, Stack } from "office-ui-fabric-react";
import { listContainerS, listS, footer } from "../../clients/components/clientsList.styles";
import { IRenderProps } from "../../orders/providers/Pagination.types";
import { sort, toggleFiltration } from "../../../config/redux/filters/filterActions";
import Pagination from "../providers/Pagination";
import { containersColumns } from "../columns/containersColumns";
import { IContainerListProps } from "../models/ContainersList.types";
import { setContainers } from "../../../config/redux/container/containersActions";
import moment from "moment";

const ContainersList: React.FC<IContainerListProps> = ({ isPanelOpen }) => {
    const { t } = useTranslation("admin");
    const dispatch = useDispatch();
    const { selectedContainers, reload } = useSelector((state: RootStateOrAny) => state.containers);

    const [cols, setCols] = useState<IColumn[]>(() => containersColumns(t));

    const footerRef = useRef<HTMLDivElement>(null);

    const selection = useRef(new Selection({
        onSelectionChanged: () => {
            const items = selection.current.getSelection();
            const ids = items.map((container: any) => ({
                id: container.id,
                key: container.id,
                closestCompanyName: container.closestCompanyName,
                closestCompanyTaxNumber: container.closestCompanyTaxNumber,
                closestReceptionPlaceAddress: container.closestReceptionPlaceAddress,
                closestReceptionPlaceType: container.closestReceptionPlaceType,
                closestReceptionPlaceDistance: container.closestReceptionPlaceDistance,
                iccid: container.iccid,
                imei: container.imei,
                lastActiveDate: moment(container.lastActiveDate).format('DD.MM.YYYY HH:mm:ss'),
                lastActiveDateStr: container.lastActiveDateStr,
                latitude: container.latitude,
                longitude: container.longitude,
                serNo: container.serNo,
                status: container.status,
                statusString: container.statusString,
                containerNumber: container.containerNumber,
                currentBatteryLevel: container.currentBatteryLevel,
                description: container.description,
                hasPhotos: container.hasPhotos,
                internalStoreName: container.internalStoreName,
                currentBatteryStatus: container.currentBatteryStatus,
                currentBatteryStatusString: container.currentBatteryStatusString,
                assignedInternalStoreName: container.assignedInternalStoreName,
            }));
            dispatch(setContainers([...ids]));
        },
    }));

    useEffect(() => {
        setCols(containersColumns(t));
    }, [t]);

    useEffect(() => {
        if (reload || !selectedContainers[0]) {
            selection.current.selectToKey("", true);
        }
    }, [reload, selectedContainers]);

    useEffect(() => {
        return () => {
            dispatch(setContainers([]));
        };
    }, []);

    const onColumnHeaderClick = (_: any, column: any) => {
        const newColumns = cols.slice();
        const currColumn = newColumns.find(c => c.key === column.key)!;
        newColumns.forEach(col => {
            if (col === currColumn) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                col.isSorted = false;
                col.isSortedDescending = true;
            }
        });
        dispatch(toggleFiltration({ filter: true }));
        dispatch(sort({ column: currColumn }));
        setCols(newColumns);
    };

    return (
        <>
            <Pagination
                footerRef={footerRef}
                render={({ data, loading }: IRenderProps) => (
                    <>
                        <Stack styles={listContainerS}>
                            <MarqueeSelection isEnabled={!isPanelOpen} selection={selection.current}>
                                <ShimmeredDetailsList
                                    items={data}
                                    columns={cols}
                                    setKey="items"
                                    layoutMode={DetailsListLayoutMode.justified}
                                    selection={selection.current}
                                    selectionPreservedOnEmptyClick={true}
                                    usePageCache={true}
                                    styles={listS}
                                    onColumnHeaderClick={onColumnHeaderClick}
                                    enableShimmer={loading}
                                    checkboxVisibility={CheckboxVisibility.onHover}
                                    selectionMode={SelectionMode.single}
                                />
                            </MarqueeSelection>
                        </Stack>
                        <div className={footer} ref={footerRef} />
                    </>
                )}
            />
        </>
    );
};

export default ContainersList;